/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-runloop */
import { isDestroyed, isDestroying } from '@ember/destroyable';
import { action, computed, set } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate, willDestroy } from '@ember/render-modifiers';
import { scheduleOnce } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import Component from '@glimmer/component';
import { getCategoricalColors } from 'frontend-utils/utils/css-variable';
import * as Highcharts from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import highchartsPatternFill from 'highcharts/modules/pattern-fill';
import highchartsSolidGauge from 'highcharts/modules/solid-gauge';

highchartsMore(Highcharts);
highchartsPatternFill(Highcharts);
highchartsSolidGauge(Highcharts);

// @ts-expect-error: Incorrect type
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getSeriesMap(seriesGroup) {
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
  return seriesGroup.reduce((seriesMap, seriesItem) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    seriesMap[seriesItem.name] = seriesItem;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return seriesMap;
  }, {});
}

// @ts-expect-error: Incorrect type
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getSeriesChanges(contentSeries, series) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const updatedKeys = Object.keys(contentSeries).filter((key) => {
    const isValidKey = key !== 'data' && key.charAt(0) !== '_';
    const isValidType =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      ['object', 'function'].indexOf(typeof contentSeries[key]) === -1;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const isTheSame = contentSeries[key] === series[key];

    return isValidKey && isValidType && !isTheSame;
  });

  // returns a list of updated keys
  return updatedKeys;
}

interface GeneralChartsOtaChartSignature {
  Args: {
    chartOptions: Highcharts.Options;
    model: object;
    onUpdateChartReference?: (chart: Highcharts.Chart) => void;
  };
  Element: HTMLDivElement;
}

// https://emberway.io/using-highcharts-js-in-an-ember-app-18a65d611644#.7oko5oxrw
export default class GeneralChartsOtaChart extends Component<GeneralChartsOtaChartSignature> {
  @service declare dates: Services['dates'];
  @service declare intl: Services['intl'];

  chart = null;
  element = undefined;

  // Default marker object - currently null so as to set the highcharts default marker
  globalDefaultMarker = null;

  @computed('args.{model.@each.isLoaded,chartOptions}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get buildOptions() {
    const chartOptions = this.args.chartOptions ?? {};
    // @ts-expect-error: Incorrect type
    const chartmodel = this.args.model?.length
      ? this.args.model
      : [
          {
            id: 'noData',
            data: 0,
            color: 'var(--prism-color-interactive-text-disabled)',
            showInLegend: false,
          },
        ];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    chartmodel[0].zIndex = 10;
    const defaults = {
      series: chartmodel,
    };

    return Object.assign(defaults, chartOptions);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildTheme() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    Highcharts.AST.allowedAttributes.push(...['alt', 'viewBox', 'xmlns']);

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line import-x/namespace
    Highcharts.dateFormats = {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      W: (timestamp) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const date = new Date(timestamp);
        const day = date.getUTCDay() === 0 ? 7 : date.getUTCDay();

        date.setDate(date.getUTCDate() + 4 - day);

        const dayNumber = Math.floor(
          // @ts-expect-error: Incorrect type
          (date.getTime() - new Date(date.getUTCFullYear(), 0, 1, -6)) /
            86400000,
        );

        return 1 + Math.floor(dayNumber / 7);
      },
    };

    /*
      Test: Base colors + pastel:
      https://github.com/appcues/highcharts-themes/blob/master/src/highcharts-themes.coffee
    */
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line import-x/namespace
    Highcharts.theme = {
      colors: getCategoricalColors(),
      plotOptions: {
        columnrange: {
          color: 'var(--prism-color-chart-sequential-info-01)',
          opacity: 0.4,
        },
        area: {
          fillColor: 'var(--prism-color-chart-sequential-info-01)',
          opacity: 0.4,
        },
        line: {
          lineWidth: 3,
        },
        bar: {
          pointWidth: 1,
        },
        column: {
          pointWidth: 1,
        },
        series: {
          dataGrouping: {
            enabled: false,
          },
        },
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
      chart: {
        style: {
          fontFamily: 'inherit',
        },
      },
      xAxis: {
        lineColor: 'var(--prism-color-border-neutral-default)',
        tickColor: 'var(--prism-color-border-neutral-default)',
        title: {
          style: {
            color: 'var(--prism-color-text-neutral-subdued)',
          },
        },
      },
      yAxis: {
        gridLineColor: 'var(--prism-color-border-neutral-default)',
        title: {
          style: {
            color: 'var(--prism-color-text-neutral-subdued)',
          },
        },
      },
      credits: false,
      legend: {
        enabled: true,
        borderRadius: 0,
        borderWidth: 0,
        align: 'center',
        x: 15,
        itemMarginTop: 4,
        itemMarginBottom: 4,
        itemStyle: {
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
      title: {
        style: {
          display: 'none',
        },
      },
    };

    Highcharts.setOptions(Highcharts.theme);
    // @ts-expect-error: Incorrect type
    Highcharts.setOptions({
      chart: {
        backgroundColor: 'var(--prism-color-elevation-surface)',
        animation: macroCondition(isTesting()) ? false : undefined,
        // Workaround to let the chart height fill the container (awaiting FE-1653)
        // Set the chart.height to null and pass class="h-full" to GeneralChartsOtaChart
        // See e.g. apps/lumen/app/components/parity/charts/chart-variance
        height: 450,
        zoomType: 'x',
        pinchType: 'none',
        resetZoomButton: {
          theme: {
            fill: 'white',
            stroke: 'silver',
            r: 0,
            states: {
              hover: {
                fill: 'var(--prism-color-chart-categorical-01)',
                style: {
                  color: 'var(--prism-color-text-inverse)',
                },
              },
            },
          },
        },
      },
      yAxis: {
        opposite: false,
        labels: {
          useHTML: true,
          style: {
            'font-size': '11px', // James Webb small text
            color: 'var(--prism-color-text-neutral-subdued)',
          },
        },
      },
      xAxis: {
        labels: {
          style: {
            'font-size': '11px', // James Webb small text
            color: 'var(--prism-color-text-neutral-subdued)',
          },
        },
        dateTimeLabelFormats: this.dates.highchartsLabelFormats,
      },
      plotOptions: {
        series: {
          animation: macroCondition(isTesting()) ? false : undefined,
          marker: {
            enabled: true,
            lineWidth: 3,
            radius: 4,
            fillColor: null,
            lineColor: 'var(--prism-color-border-inverse-emphasis)',
            symbol: 'circle',
            states: {
              hover: {
                lineWidth: 0,
                radius: null,
                radiusPlus: 0,
              },
            },
          },
          states: {
            inactive: {
              opacity: 1,
            },
          },
          stickyTracking: false,
          dataLabels: {
            y: -25,
          },
        },
      },
      legend: {
        enabled: true,
        itemStyle: {
          'font-size': '12px', // James Webb label
          'font-weight': '500', // James Webb label
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
        itemHiddenStyle: {
          color: 'var(--prism-color-interactive-text-disabled)',
        },
      },
      lang: {
        months: [
          this.intl.t('months.jan'),
          this.intl.t('months.feb'),
          this.intl.t('months.mar'),
          this.intl.t('months.apr'),
          this.intl.t('months.may'),
          this.intl.t('months.jun'),
          this.intl.t('months.jul'),
          this.intl.t('months.aug'),
          this.intl.t('months.sep'),
          this.intl.t('months.oct'),
          this.intl.t('months.nov'),
          this.intl.t('months.dec'),
        ],
        weekdays: [
          this.intl.t('days.sun').toString(),
          this.intl.t('days.mon').toString(),
          this.intl.t('days.tue').toString(),
          this.intl.t('days.wed').toString(),
          this.intl.t('days.thu').toString(),
          this.intl.t('days.fri').toString(),
          this.intl.t('days.sat').toString(),
        ],
        shortMonths: [
          this.intl.t('months_short.jan').toString(),
          this.intl.t('months_short.feb').toString(),
          this.intl.t('months_short.mar').toString(),
          this.intl.t('months_short.apr').toString(),
          this.intl.t('months_short.may').toString(),
          this.intl.t('months_short.jun').toString(),
          this.intl.t('months_short.jul').toString(),
          this.intl.t('months_short.aug').toString(),
          this.intl.t('months_short.sep').toString(),
          this.intl.t('months_short.oct').toString(),
          this.intl.t('months_short.nov').toString(),
          this.intl.t('months_short.dec').toString(),
        ],
        downloadPNG: `${this.intl.t('highcharts.download')} PNG`,
        downloadJPEG: `${this.intl.t('highcharts.download')} JPEG`,
        downloadPDF: `${this.intl.t('highcharts.download')} PDF`,
        downloadSVG: `${this.intl.t('highcharts.download')} SVG`,
        exportButtonTitle: this.intl.t('highcharts.export'),
        loading: this.intl.t('general.loading'),
        printButtonTitle: this.intl.t('highcharts.print'),
        printChart: this.intl.t('highcharts.print'),
      },
      credits: false,
      exporting: {
        enabled: false,
      },
      title: {
        text: '',
        x: -20, // center
      },
      tooltip: {
        snap: 0,
        // similar as shared, needs to be false by default
        split: false,
        backgroundColor: 'transparent',
      },
    });

    // Define a custom symbol path
    // https://stackoverflow.com/questions/27542928/extend-highcharts-renderer-symbols-to-have-plus-sign
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-unsafe-return
    Highcharts.SVGRenderer.prototype.symbols.cross = (x, y, w, h) => [
      'M',
      x,
      y,
      'L',
      x + w,
      y + h,
      'M',
      x + w,
      y,
      'L',
      x,
      y + h,
      'z',
    ];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-unsafe-return
    Highcharts.SVGRenderer.prototype.symbols.arrowUp = (x, y) => [
      'm',
      x,
      y,
      'l',
      4,
      -4,
      'l',
      4,
      4,
      'm',
      -4,
      -4,
      'l',
      0,
      8,
      'Z',
    ];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-unsafe-return
    Highcharts.SVGRenderer.prototype.symbols.arrowDown = (x, y) => [
      'm',
      x,
      y,
      'l',
      4,
      4,
      'l',
      4,
      -4,
      'm',
      -4,
      -4,
      'l',
      0,
      8,
      'Z',
    ];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    Highcharts.SVGRenderer.prototype.symbols.bar = function (x, y, w, h) {
      // RI Staydate bar marker with rounded edges
      // Select a line thickness
      const lineThickness = 4;

      // Make a rounded corner line SVG object
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return [
        'M',
        x - 1 + w / 10,
        y + h / 2, // Start at lower-left curve point, capital letter M = absolute positions! Start at middle (h / 2) of marker space. (x - 1) to nudge marker a bit to the left of its bar
        'c',
        -(w / 10),
        0,
        -(w / 10),
        -lineThickness,
        0,
        -lineThickness, // Make a curve to the top-left point (180 degrees upwards), small letter c = relative positions!
        'l',
        w - w / 10,
        0, // Make straight line to top-right side start of curve
        'c',
        w / 10,
        0,
        w / 10,
        lineThickness,
        0,
        lineThickness,
        'z', // Make curve to bottom right part
      ];
    };

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    Highcharts.SVGRenderer.prototype.symbols.rectangle = function (x, y, w, h) {
      // Make a rounded corner line SVG object
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return [
        'M',
        x,
        y + h / 2, // Start at upper-left curve point, capital letter M = absolute positions! Start at middle (h / 2) of marker space.
        'H',
        x + w, // horizontal line to the right
        'V',
        y + h / 2 + 0.1, // vertical line down
        'H',
        x, // horizontal line left
        'L',
        x,
        y + h / 2,
        'z', // Line to start
      ];
    };

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line import-x/namespace
    if (Highcharts.VMLRenderer) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, import-x/namespace
      Highcharts.VMLRenderer.prototype.symbols.cross =
        Highcharts.SVGRenderer.prototype.symbols.cross;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, import-x/namespace
      Highcharts.VMLRenderer.prototype.symbols.bar =
        Highcharts.SVGRenderer.prototype.symbols.bar;
    }
  }

  draw(): void {
    if (isDestroyed(this) || isDestroying(this)) {
      return;
    }

    if (this.element === undefined) {
      return;
    }

    const options = this.buildOptions;

    set(this, 'chart', Highcharts.chart(this.element, options));

    // @ts-expect-error: Incorrect type
    this.args.onUpdateChartReference?.(this.chart);
  }

  drawLater(): void {
    // @ts-expect-error: Incorrect type
    scheduleOnce('afterRender', this, 'draw');
  }

  @action
  chartOptionsObserver(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.chart?.update(this.buildOptions);
  }

  @action
  // @ts-expect-error: Incorrect type
  didInsertChartElement(element): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.element = element;
    this.drawLater();
    this.buildTheme();
  }

  @action
  didReceiveChartAttrs(): void {
    // Code based on Ember-Highcharts, but adjusted for our own needs
    // https://github.com/ahmadsoe/ember-highcharts/blob/master/addon/components/high-charts.js
    const { chart } = this;
    const { model } = this.args;

    if (!model || !chart) {
      return;
    }

    // If axis change, do a complete redraw
    // The initial check is to make sure we are dealing with an array
    // It's possible that the yAxis is an object
    // This means it's always going to be one axis and no complete draw is needed
    // TODO: SH-12652 should not redraw for charts with 1 y axis passed as an object, but it currently breaks the pace chart so the check has been reverted for now
    if (
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      chart?.options?.yAxis?.length !== this.args.chartOptions?.yAxis?.length
    ) {
      // @ts-expect-error: Incorrect type
      this.draw(this.element);
      return;
    }

    // if the order of the series has changed, we can't just update, we need a complete redraw
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const isDifferentOrder = model.some(
      // @ts-expect-error: Incorrect type
      (series, idx) =>
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        chart.series[idx] && chart.series[idx].name !== series.name,
    );
    if (isDifferentOrder) {
      this.draw();
      return;
    }

    // create maps to make series data easier to work with
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const contentSeriesMap = getSeriesMap(model);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const chartSeriesMap = getSeriesMap(chart.series);

    // remove and update current series
    // @ts-expect-error: Incorrect type
    const chartSeriesToRemove = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    chart.series.forEach((series) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const contentSeries = contentSeriesMap[series.name];

      if (!contentSeries) {
        chartSeriesToRemove.push(series);
        return;
      }

      const updatedKeys = getSeriesChanges(contentSeries, series);

      // call series.update() when other series attributes like pointStart have changed
      if (updatedKeys.length) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        series.update(contentSeries, false);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        series.setData(contentSeries.data, false);
      }
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    chartSeriesToRemove.forEach((series) => series.remove(false));

    // add new series
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    model.forEach((contentSeries) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, no-prototype-builtins
      if (!chartSeriesMap.hasOwnProperty(contentSeries.name)) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        chart.addSeries(contentSeries, false);
      }
    });

    // In some charts (e.g. chart-stay-date) we overwrite the default zoom to enable filtering
    // Because of this we need to actually zoom out, otherwise highCharts won't zoom out by itself
    // This also assumes there is only one xAxis.
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const { dataMax, dataMin, userMax, userMin } = chart.xAxis[0].getExtremes();

    // userMin and userMax show the range the user zoomed into. If they are undefined, there is no zoom
    if (userMax && userMin && dataMax !== userMax && dataMin !== userMin) {
      // https://api.highcharts.com/class-reference/Highcharts.Axis#setExtremes
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      chart.xAxis[0].setExtremes();
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    chart.redraw();
  }

  @action
  willDestroyChart(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.chart?.destroy();
    set(this, 'chart', null);
    // @ts-expect-error: Incorrect type
    this.args.onUpdateChartReference?.(this.chart);
    this.element = undefined;
  }

  <template>
    <div
      class="highcharts-wrapper"
      data-test-id="highcharts-wrapper"
      {{didInsert this.didInsertChartElement}}
      {{didUpdate this.didReceiveChartAttrs @chartOptions @model}}
      {{didUpdate this.chartOptionsObserver this.buildOptions}}
      {{willDestroy this.willDestroyChart}}
      ...attributes
    >
    </div>
  </template>
}
